/**
 * Badge Component - HELIX Professional Design System v9.2.1
 *
 * EXECUTIVE READABILITY PASS:
 * - Default size: 12px (was 10px)
 * - Small size: 11px (minimum for any text)
 * - Badges/chips: 11-12px per executive typography scale
 *
 * Variants:
 * - default: Neutral gray (for general labels)
 * - outline: Border only (subtle)
 * - keep: Green (success/on-track status)
 * - eliminate: Red (error/critical status)
 * - defer: Amber (warning/at-risk status)
 * - pending: Gray (neutral/pending status)
 * - accent: Muted blue (emphasis/feature tag)
 *
 * Status badges use {main + light + border} per Executive OS design.
 * These are for INDICATORS only, never for CTAs.
 */

import * as React from 'react';
import { cn } from '@/lib/cn';

type BadgeVariant =
  | 'default'
  | 'outline'
  | 'keep'
  | 'eliminate'
  | 'defer'
  | 'pending'
  | 'accent'
  // Back-compat variants used across ui-golden ports.
  | 'success'
  | 'positive'
  | 'warning'
  | 'critical';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

/**
 * Status badge styles using Executive OS color tokens.
 * Format: bg (light) + text (main) + border
 */
const variantClasses: Record<BadgeVariant, string> = {
  // Neutral: muted background
  default: 'bg-muted text-muted-foreground border border-transparent',
  // Outline: border only
  outline: 'bg-transparent text-foreground border border-border',
  // Keep/Success: green
  keep:
    'border border-[hsl(var(--helix-keep-border))] bg-[hsl(var(--helix-keep-light))] text-[hsl(var(--helix-keep))]',
  success:
    'border border-[hsl(var(--helix-keep-border))] bg-[hsl(var(--helix-keep-light))] text-[hsl(var(--helix-keep))]',
  positive:
    'border border-[hsl(var(--helix-keep-border))] bg-[hsl(var(--helix-keep-light))] text-[hsl(var(--helix-keep))]',
  // Eliminate/Error: red
  eliminate:
    'border border-[hsl(var(--helix-eliminate-border))] bg-[hsl(var(--helix-eliminate-light))] text-[hsl(var(--helix-eliminate))]',
  critical:
    'border border-[hsl(var(--helix-eliminate-border))] bg-[hsl(var(--helix-eliminate-light))] text-[hsl(var(--helix-eliminate))]',
  // Defer/Warning: amber
  defer:
    'border border-[hsl(var(--helix-defer-border))] bg-[hsl(var(--helix-defer-light))] text-[hsl(var(--helix-defer))]',
  warning:
    'border border-[hsl(var(--helix-defer-border))] bg-[hsl(var(--helix-defer-light))] text-[hsl(var(--helix-defer))]',
  // Pending/Neutral: gray
  pending:
    'border border-[hsl(var(--helix-pending-border))] bg-[hsl(var(--helix-pending-light))] text-[hsl(var(--helix-pending))]',
  // Accent: muted blue
  accent:
    'border border-[hsl(var(--helix-primary-medium))] bg-[hsl(var(--helix-primary-light))] text-primary',
};

type BadgeSize = 'default' | 'sm';

export interface BadgePropsExtended extends BadgeProps {
  size?: BadgeSize;
}

export const Badge = ({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: BadgePropsExtended) => (
  <span
    className={cn(
      // Base styles: executive-readable typographic scale
      'inline-flex items-center rounded-full font-medium leading-tight',
      // Size: default 12px, small 11px (minimum for any text)
      size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-[12px]',
      variantClasses[variant],
      className,
    )}
    {...props}
  />
);

// Re-export types
export type { BadgeVariant };
